export interface PreparePreview {
  field_name: string;
  page: number;
  box: [number, number, number, number];
}

export interface PrepareResponse {
  session_id: string;
  signed_attrs: string;
  digest_algorithm: string;
  signature_algorithm: string;
  expires_at: string;
  preview: PreparePreview;
}

export interface PrepareMeta {
  mode: "frei" | "stampit";
  field_name?: string;
  page?: number;
  box?: [number, number, number, number];
  cert_chain: string[];
  reason?: string;
  location?: string;
  contact?: string;
  with_timestamp: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
  }
}

async function throwIfError(resp: Response): Promise<Response> {
  if (resp.ok) return resp;
  let code = "UNKNOWN";
  let message = resp.statusText;
  try {
    const body = await resp.json();
    code = body.code ?? code;
    message = body.message ?? message;
  } catch {
    /* body wasn't JSON; fall back to statusText */
  }
  throw new ApiError(code, message, resp.status);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function certChainToBase64(certChainDer: Uint8Array[]): string[] {
  return certChainDer.map(bytesToBase64);
}

export function signedAttrsFromResponse(resp: PrepareResponse): Uint8Array {
  return base64ToBytes(resp.signed_attrs);
}

export interface InspectField {
  field_name: string;
  page: number;
  box: [number, number, number, number];
  filled: boolean;
}

function pdfFilename(pdf: File | Blob): string {
  return pdf instanceof File ? pdf.name : "document.pdf";
}

export async function inspect(pdf: File | Blob): Promise<InspectField[]> {
  const form = new FormData();
  form.append("pdf", pdf, pdfFilename(pdf));
  const resp = await fetch("/api/v1/inspect", { method: "POST", body: form });
  await throwIfError(resp);
  return resp.json();
}

export async function prepare(
  pdf: File | Blob,
  meta: PrepareMeta,
  stampImage?: File | Blob,
): Promise<PrepareResponse> {
  const form = new FormData();
  form.append("pdf", pdf, pdfFilename(pdf));
  form.append("meta", JSON.stringify(meta));
  if (stampImage) form.append("stamp_image", stampImage, "stamp.png");

  const resp = await fetch("/api/v1/prepare", { method: "POST", body: form });
  await throwIfError(resp);
  return resp.json();
}

export interface CompletedPdf {
  blob: Blob;
  filename: string;
}

function filenameFromContentDisposition(header: string | null, fallback: string): string {
  // Prefer the RFC 6266 filename* (full UTF-8, e.g. umlauts or "€"); the
  // plain filename= is an ASCII-lossy fallback for clients that don't
  // understand filename* (see backend/app/main.py::_content_disposition).
  const utf8Match = header?.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match) return decodeURIComponent(utf8Match[1]);

  const asciiMatch = header?.match(/filename="?([^";]+)"?/);
  return asciiMatch ? asciiMatch[1] : fallback;
}

/** Asks the backend to POST the completion webhook (server-side to avoid CORS and enforce the timeout). */
export async function notifyWebhook(url: string, itemId: string, filename: string): Promise<void> {
  const resp = await fetch("/api/v1/mappe/notify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url, item_id: itemId, filename }),
  });
  await throwIfError(resp);
}

export async function complete(
  sessionId: string,
  signature: Uint8Array,
): Promise<CompletedPdf> {
  const resp = await fetch("/api/v1/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      session_id: sessionId,
      signature: bytesToBase64(signature),
    }),
  });
  await throwIfError(resp);
  const filename = filenameFromContentDisposition(
    resp.headers.get("Content-Disposition"),
    "document_signed.pdf",
  );
  return { blob: await resp.blob(), filename };
}
